import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme } from 'styled-components'
import isEqual from 'lodash-es/isEqual'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import ScrollableTable from '../../components/exchange/ScrollableTable'
import LoadingShade from '../../components/common/LoadingShade'
import Price from '../../components/exchange/Price'
import Amount from '../../components/exchange/Amount'
import CurrentPrice from './CurrentPrice'
import { setCurrentPrice, setAmount } from '../../redux/exchange/action'
import { formatNumber } from '../../utils/number'
import { withI18n } from '../../i18n/util'

import askBidSvg from '../../static/images/red-green-D.svg'
import bidSvg from '../../static/images/green-D.svg'
import askSvg from '../../static/images/red-D.svg'

const Wrapper = styled.div`
  position: absolute;
  width: 290px;
  top: 0;
  bottom: 0;
  background: ${({ theme }) => theme.exchangeSecondBg};
  @media (max-width: 768px) {
    width: 100%;
    max-height: 400px;
    min-height: 320px;
    position: relative;
  }
`

const Head = styled.div`
  height: 30px;
  display: flex;
  box-shadow: ${({ theme }) => theme.exchangeHeadShadow};
`

const Content = styled.div`
  margin-top: 3px;
`

const ContentHD = styled.div`
  height: 20px;
  line-height: 20px;
  display: flex;
  color: #48515d;
  padding-right: 5px;
  div {
    text-align: center;
  }
  .price {
    width: 30%;
    text-align: left;
    text-indent: 5px;
  }
  .amount {
    width: 30%;
    text-align: right;
  }
  .total {
    width: 40%;
    text-align: right;
  }
`

const ContentBD = styled.div`
  position: absolute;
  top: 50px;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  flex: 0 0 280px;
  overflow: auto;
  background: ${({ theme }) => theme.exchangeSecondBg};
`

const CurrentPriceRow = styled.div`
  flex-wrap: wrap;
  flex: 0 0 29px;
`

const OrderBookPanel = styled.ul`
  display: flex;
  align-items: center;
  padding-left: 5px;
`

const OrderBookPanelItem = styled.li`
  cursor: pointer;
  padding: 0px 5px;
  margin-right: 5px;
  border-radius: 3px;
  height: 22px;
  &:hover {
    background: rgba(240, 185, 11, 0.15);
  }
  img {
    width: 22px;
  }
  background: ${props => (props.selected ? 'rgba(240,185,11,0.15)' : 'inherit')};
`

const SellOrder = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 2em;
  transition: all 0.2s ease;
  .table-wrap {
    display: flex;
    align-items: flex-end;
    .fixed-table {
      top: 7px;
      display: flex;
      align-items: ${props => (props.isScroll ? 'flex-start' : 'flex-end')};
      overflow: ${props => (props.isScroll ? 'auto' : 'hidden')};
    }
  }
`

const BuyOrder = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 2em;
  transition: all 0.2s ease;
  .table-wrap {
    display: flex;
    align-items: flex-start;
    .fixed-table {
      overflow: ${props => (props.isScroll ? 'auto' : 'hidden')};
      top: 5px;
      bottom: 7px;
      display: flex;
      align-items: flex-start;
    }
  }
`

const Total = styled.div`
  text-align: left;
  padding-right: 5px;
`

class Orderbook extends PureComponent {
  static propTypes = {
    orderBooks: PropTypes.object,
    symbolTicker: PropTypes.object,
    pairs: PropTypes.array,
    userInfo: PropTypes.object,
    currentTrade: PropTypes.string,
    dispatch: PropTypes.func,
    match: PropTypes.object,
    formatI18nText: PropTypes.func,
    theme: PropTypes.object.isRequired,
  }

  static defaultProps = {
    orderBooks: null,
    symbolTicker: {},
    pairs: [],
    userInfo: {},
    currentTrade: '',
    dispatch: () => {},
    match: {},
    formatI18nText: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      expand: '',
    }
  }

  componentDidUpdate(prevProps) {
    const { currentTrade, orderBooks } = this.props
    if (
      currentTrade !== prevProps.currentTrade ||
      (orderBooks && prevProps.orderBooks && orderBooks.symbol !== prevProps.orderBooks.symbol)
    ) {
      this._adjustSellOrder()
    }

    const currentAsks = orderBooks && orderBooks.asks
    const prevAsks = prevProps.orderBooks && prevProps.orderBooks.asks

    const currentBids = orderBooks && orderBooks.bids
    const prevBids = prevProps.orderBooks && prevProps.orderBooks.bids

    if (!isEqual(currentAsks, prevAsks) || !isEqual(currentBids, prevBids)) {
      this._adjustSellOrder()
    }
  }

  _setAmount = (price, amount, type) => {
    const { dispatch, userInfo, currentTrade, symbolTicker } = this.props
    if (!currentTrade) return

    amount = parseFloat(amount)
    dispatch(setCurrentPrice(price))

    const baseAsset = currentTrade.split('_')[0]
    const quotaAsset = currentTrade.split('_')[1]
    const balances = userInfo.balances || []
    const quotaAssetBalance = balances.find(b => b.symbol && b.symbol === quotaAsset) || {
      free: 0,
      frozen: 0,
      locked: 0,
    }
    const baseAssetBalance = balances.find(b => b.symbol && b.symbol === baseAsset) || {
      free: 0,
      frozen: 0,
      locked: 0,
    }

    const amountObj = {}
    if (type === 'ask') {
      const maxBuyAmount = parseFloat(quotaAssetBalance.free) / parseFloat(symbolTicker.price)

      amountObj.buyAmount = maxBuyAmount > amount ? amount : maxBuyAmount
    } else {
      amountObj.sellAmount =
        parseFloat(baseAssetBalance.free) > amount ? amount : parseFloat(baseAssetBalance.free)
    }
    dispatch(setAmount(amountObj))
  }

  _buildColum = () => {
    const { formatI18nText, theme } = this.props
    const columns = [
      {
        title: formatI18nText('exchange.price'),
        headerAlign: 'left',
        dataIndex: 'price',
        sorter: true,
        width: '30%',
        key: 'Price',
        render: item => (
          <Price
            value={item.price}
            canSetPrice={true}
            priceColor={item.type === 'ask' ? theme.down : theme.up}
          />
        ),
      },
      {
        title: formatI18nText('exchange.amount'),
        dataIndex: 'amount',
        headerAlign: 'right',
        width: '30%',
        sorter: true,
        key: 'Amount',
        render: item => (
          <div style={{ paddingRight: '2px' }}>
            <Amount
              onClick={this._setAmount}
              value={item.amount}
              price={item.price}
              type={item.type}
              textAlign="flex-end"
            />
          </div>
        ),
      },
      {
        title: formatI18nText('exchange.total'),
        dataIndex: 'total',
        width: '40%',
        headerAlign: 'right',
        sorter: true,
        key: 'Total',
        render: item => (
          /* eslint-disable-next-line */
          <Total>
            <Price
              value={item.total}
              priceColor={theme.exchangeCommonNumberColor}
              alignContent="flex-end"
              format={formatNumber}
            />
          </Total>
        ),
      },
    ]

    return columns
  }

  _onConnect = () => {}

  _buildAsks = () => {
    const { orderBooks } = this.props
    const result = []
    const latestBook = orderBooks || { asks: [] }
    latestBook.asks.forEach(ask => {
      result.push({
        price: ask[0],
        amount: ask[1],
        total: ask[0] * ask[1],
        type: 'ask',
      })
    })
    return this._reverseArray(result)
  }

  _buildBids = () => {
    const { orderBooks } = this.props
    const result = []
    const latestBook = orderBooks || { bids: [] }
    latestBook.bids.forEach(bid => {
      result.push({
        price: bid[0],
        amount: bid[1],
        total: bid[0] * bid[1],
        type: 'bid',
      })
    })

    return result
  }

  _reverseArray = arr => {
    const newArr = []
    for (let i = arr.length - 1; i >= 0; i--) {
      newArr.push(arr[i])
    }
    return newArr
  }

  _selectAsks = () => {
    this.setState({ expand: 'asks' })
    this._adjustSellOrder()
  }

  _selectBoth = () => {
    this.setState({ expand: '' })
    this._adjustSellOrder()
    this._adjustBuyOrder()
  }

  _adjustSellOrder = () => {
    setTimeout(() => {
      try {
        const sellOrderDiv = document.querySelector('.sell-order') || {}
        const fixTable = sellOrderDiv.querySelector('.fixed-table') || {}
        const innerTable = fixTable.querySelector('table') || {}
        if (innerTable.offsetHeight <= fixTable.offsetHeight) {
          fixTable.style.alignItems = 'flex-end'
        } else {
          fixTable.style.alignItems = 'flex-start'
          fixTable.scrollTop = innerTable.offsetHeight + 200
        }
      } catch (err) {
        console.log(err)
      }
    })
  }

  _adjustBuyOrder = () => {
    setTimeout(() => {
      try {
        const sellOrderDiv = document.querySelector('.buy-order') || {}
        const fixTable = sellOrderDiv.querySelector('.fixed-table') || {}
        fixTable.style.alignItems = 'flex-start'
        fixTable.scrollTop = 0
      } catch (err) {
        console.log(err)
      }
    })
  }

  _getMedian = () => {
    const { orderBooks } = this.props
    const bids = orderBooks?.bids || []
    const asks = orderBooks?.asks || []

    const sorted = bids
      .map(bid => bid[1])
      .concat(asks.map(ask => ask[1]))
      .sort((a, b) => a - b)

    const medianIdx = (sorted.length * 3) / 4
    return Number(sorted[medianIdx])
  }

  render() {
    const { orderBooks, currentTrade, formatI18nText, theme } = this.props
    const asks = this._buildAsks()
    const bids = this._buildBids()
    const median = this._getMedian()
    const columns = this._buildColum()
    const { expand } = this.state

    return (
      <Wrapper>
        <Head>
          <OrderBookPanel>
            <OrderBookPanelItem onClick={this._selectBoth} selected={!expand}>
              <img src={askBidSvg} alt="" />
            </OrderBookPanelItem>
            <OrderBookPanelItem
              onClick={() => {
                this.setState({ expand: 'bids' })
              }}
              selected={expand === 'bids'}
            >
              <img src={bidSvg} alt="" />
            </OrderBookPanelItem>
            <OrderBookPanelItem onClick={this._selectAsks} selected={expand === 'asks'}>
              <img src={askSvg} alt="" />
            </OrderBookPanelItem>
          </OrderBookPanel>
        </Head>
        <Content>
          <ContentHD>
            <div className="price">{formatI18nText('exchange.price')}</div>
            <div className="amount">{formatI18nText('exchange.amount')}</div>
            <div className="total">{formatI18nText('exchange.total')}</div>
          </ContentHD>
          <ContentBD>
            {expand !== 'bids' && (
              <SellOrder isScroll={expand !== ''} className="sell-order">
                <ScrollableTable
                  columns={columns}
                  dataSource={asks}
                  hideHeader={true}
                  withShadow={true}
                  median={median}
                  isFixedHeader={true}
                  shadowDataIndex="amount"
                  shadowBg={theme.downLight}
                />
                {!orderBooks && currentTrade && <LoadingShade />}
              </SellOrder>
            )}
            <CurrentPriceRow>
              <CurrentPrice />
            </CurrentPriceRow>
            {expand !== 'asks' && (
              <BuyOrder isScroll={expand !== ''} className="buy-order">
                <ScrollableTable
                  columns={columns}
                  dataSource={bids}
                  hideHeader={true}
                  isFixedHeader={true}
                  withShadow={true}
                  median={median}
                  shadowDataIndex="amount"
                  shadowBg={theme.upLight}
                />
                {!orderBooks && currentTrade && <LoadingShade />}
              </BuyOrder>
            )}
          </ContentBD>
        </Content>
      </Wrapper>
    )
  }
}

const mapStateToProps = ({
  exchange: { currentTrade },
  tradePairs: { pairs },
  orderBooks,
  tickers: { symbolTickers },
  account: { userInfo },
}) => ({
  userInfo,
  currentTrade,
  orderBooks: orderBooks && orderBooks[currentTrade],
  symbolTicker: symbolTickers.find(s => s.symbol === currentTrade) || {},
  pairs,
})

export default withI18n(connect(mapStateToProps)(withRouter(withTheme(Orderbook))))
